import {
  Body,
  Caption,
  EmptyState,
  SectionTitle,
  VAlert,
  VBadge,
  VButton,
  VDialog,
} from '@vrzl/ui';
import { LayoutGrid, Plus } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';

import { getSelectedTenantLabel } from '@/domains/auth';

import { useSaveOverviewConfig } from '../api/mutations';
import type { OverviewBlock } from '../api/types';
import {
  blockErrors,
  blockLabel,
  blocksContaining,
  emptyBlock,
  followIndex,
  hasErrors,
  moveBlock,
  toSavedBlocks,
} from '../utils/editBlocks';
import { saveErrorMessage } from '../utils/saveError';

import { BlockEditorPanel } from './blockEditorPanel';
import { BlockPreviewCard } from './blockPreviewCard';
import type { CriterionOption } from './criterionPicker';
import { OverviewLayout } from './overviewGrid';
import { RowHint } from './rowHint';

type EditOverviewDialogProps = {
  initialBlocks: OverviewBlock[];
  options: readonly CriterionOption[];
  perspective: { id: string; name: string };
  onClose: () => void;
};

/**
 * Werkt op een lokale kopie; "Opslaan" stuurt de hele lijst in één keer. Sluiten
 * (Annuleren, X of Escape) gooit de kopie weg. Bij een fout blijft de modal open.
 */
export function EditOverviewDialog({
  initialBlocks,
  options,
  perspective,
  onClose,
}: EditOverviewDialogProps) {
  // Elk blok krijgt een client-id als React-key: zo volgen kaart en focus het blok bij verplaatsen.
  const [items, setItems] = useState<{ id: string; block: OverviewBlock }[]>(() =>
    initialBlocks.map((block) => ({ id: crypto.randomUUID(), block })),
  );
  const blocks = useMemo(() => items.map((item) => item.block), [items]);
  const [selected, setSelected] = useState<number | null>(initialBlocks.length > 0 ? 0 : null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const dragFrom = useRef<number | null>(null);
  const save = useSaveOverviewConfig(perspective.id);

  const nameByCode = useMemo(() => new Map(options.map((o) => [o.code, o.name])), [options]);
  const knownCodes = useMemo(() => new Set(nameByCode.keys()), [nameByCode]);
  const invalid = hasErrors(blocks, knownCodes);
  const current = selected === null ? undefined : blocks[selected];
  const tenantLabel = getSelectedTenantLabel();

  const move = (from: number, to: number) => {
    if (to < 0 || to >= blocks.length || from === to) return;
    setItems((prev) => moveBlock(prev, from, to));
    setSelected((s) => (s === null ? s : followIndex(s, from, to)));
  };

  const updateCurrent = (block: OverviewBlock) => {
    if (selected === null) return;
    setItems((prev) => prev.map((item, i) => (i === selected ? { ...item, block } : item)));
  };

  const addBlock = () => {
    setItems((prev) => [...prev, { id: crypto.randomUUID(), block: emptyBlock() }]);
    setSelected(blocks.length);
  };

  const removeCurrent = () => {
    if (selected === null) return;
    const next = items.filter((_, i) => i !== selected);
    setItems(next);
    setSelected(next.length === 0 ? null : Math.min(selected, next.length - 1));
    setConfirmRemove(false);
  };

  const submit = () => save.mutate(toSavedBlocks(blocks), { onSuccess: onClose });

  return (
    <>
      <VDialog
        open
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
        size="xl"
        title="Overview bewerken"
        titleHidden
        description="Stel de blokken van de Overview samen voor jouw organisatie en perspectief."
        descriptionHidden
        headerStart={
          <div className="flex items-center gap-2">
            <Body as="span" weight="semibold" className="text-lg">
              Overview bewerken
            </Body>
            <VBadge color="success">
              {tenantLabel ? `${tenantLabel} · ${perspective.name}` : perspective.name}
            </VBadge>
          </div>
        }
        footer={
          <div className="flex items-center gap-3">
            <Caption>Wijzigingen gelden pas na &quot;Opslaan&quot;.</Caption>
            <div className="ml-auto flex gap-2">
              <VButton type="button" variant="ghost" onClick={onClose}>
                Annuleren
              </VButton>
              <VButton
                type="button"
                variant="primary"
                disabled={invalid || save.isPending}
                onClick={submit}
              >
                {save.isPending ? 'Opslaan…' : 'Opslaan'}
              </VButton>
            </div>
          </div>
        }
      >
        <div className="flex min-h-0 flex-1">
          <aside className="w-95 shrink-0 overflow-y-auto border-r border-border/50 p-4">
            {current && selected !== null ? (
              <BlockEditorPanel
                block={current}
                index={selected}
                count={blocks.length}
                label={blockLabel(current, nameByCode)}
                errors={blockErrors(current, knownCodes)}
                options={options}
                nameByCode={nameByCode}
                usedIn={(code) => blocksContaining(code, blocks, nameByCode)}
                onChange={updateCurrent}
                onAddBlock={addBlock}
                onRemove={() => setConfirmRemove(true)}
              />
            ) : (
              <div className="flex flex-col items-start gap-3">
                <Caption>Kies rechts een blok om het te bewerken, of voeg een blok toe.</Caption>
                <VButton type="button" variant="outline" icon={Plus} onClick={addBlock}>
                  Blok toevoegen
                </VButton>
              </div>
            )}
          </aside>

          <section className="flex min-w-0 flex-1 flex-col gap-3 overflow-y-auto bg-muted p-4">
            <div>
              <SectionTitle>Indeling van de Overview</SectionTitle>
              <Caption>
                Sleep een blok of gebruik de pijltjes om de volgorde te wijzigen. De hoogte op de
                echte Overview hangt af van de gevonden gegevens.
              </Caption>
            </div>

            {save.error && (
              <VAlert variant="error" title="Opslaan mislukt">
                {saveErrorMessage(save.error)}
              </VAlert>
            )}

            {blocks.length === 0 ? (
              <EmptyState
                icon={LayoutGrid}
                title="Nog geen blokken"
                description="Voeg een blok toe om de Overview in te richten."
              />
            ) : (
              <OverviewLayout
                fixedColumns
                widths={blocks.map((block) => block.width)}
                getKey={(index) => items[index]!.id}
                itemProps={(index) => ({
                  onDragOver: (event) => event.preventDefault(),
                  onDrop: (event) => {
                    event.preventDefault();
                    if (dragFrom.current !== null) move(dragFrom.current, index);
                    dragFrom.current = null;
                  },
                })}
                renderRowGap={({ used, empty }) => <RowHint used={used} empty={empty} />}
                renderItem={(index) => (
                  <BlockPreviewCard
                    block={blocks[index]!}
                    index={index}
                    count={blocks.length}
                    label={blockLabel(blocks[index]!, nameByCode)}
                    nameByCode={nameByCode}
                    selected={index === selected}
                    error={blockErrors(blocks[index]!, knownCodes).criteria}
                    onSelect={() => setSelected(index)}
                    onMove={(to) => move(index, to)}
                    onDragStart={() => {
                      dragFrom.current = index;
                    }}
                    onDragEnd={() => {
                      dragFrom.current = null;
                    }}
                  />
                )}
              />
            )}
          </section>
        </div>
      </VDialog>

      <VDialog
        open={confirmRemove}
        onOpenChange={setConfirmRemove}
        size="sm"
        title="Blok verwijderen"
        description="Bevestig het verwijderen van dit blok."
        descriptionHidden
        footer={
          <div className="flex justify-end gap-2">
            <VButton type="button" variant="outline" onClick={() => setConfirmRemove(false)}>
              Annuleren
            </VButton>
            <VButton type="button" variant="destructive" onClick={removeCurrent}>
              Verwijderen
            </VButton>
          </div>
        }
      >
        <div className="px-4 py-3">
          <Body>
            Weet je zeker dat je het blok{' '}
            <span className="font-medium">{current ? blockLabel(current, nameByCode) : ''}</span>{' '}
            wilt verwijderen? Dit wordt pas definitief na &quot;Opslaan&quot;.
          </Body>
        </div>
      </VDialog>
    </>
  );
}
